import React, { useState } from 'react';
import { X, Calendar, CircleAlert } from 'lucide-react';
import type { Engagement } from '../types';
import { 
  formatDate, 
   
  validateRequired,
  validateDate,
  validateTaxForm,
  TAX_FORMS
} from '../utils/format';
import { db } from '../db/database';

interface EngagementModalProps {
  mode: 'new' | 'edit' | 'duplicate';
  engagement?: Engagement;
  onClose: () => void;
  onSave: (engagement: Engagement) => Promise<void>;
}

export const EngagementModal: React.FC<EngagementModalProps> = ({
  mode,
  engagement,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Partial<Engagement>>({
    clientName: engagement?.clientName || '',
    taxYearEnd: engagement?.taxYearEnd || '',
    taxForm: engagement?.taxForm || '',
    description: mode === 'duplicate' ? '' : (engagement?.description || ''),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [shake, setShake] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dateInputRef = React.useRef<HTMLInputElement>(null);
  const hiddenDateInputRef = React.useRef<HTMLInputElement>(null);

  const isEditMode = mode === 'edit';
  const isDuplicateMode = mode === 'duplicate';

  const validateForm = async (): Promise<boolean> => {
    const newErrors: Record<string, string> = {};

    // Validate client name
    const clientNameError = validateRequired(formData.clientName);
    if (clientNameError) {
      newErrors.clientName = clientNameError;
    }

    // Validate tax year end
    const dateError = validateDate(formData.taxYearEnd);
    if (dateError) {
      newErrors.taxYearEnd = dateError;
    }

    // Validate tax form
    const taxFormError = validateTaxForm(formData.taxForm);
    if (taxFormError) {
      newErrors.taxForm = taxFormError;
    }

    // In duplicate mode, description is required
    if (isDuplicateMode && !formData.description?.trim()) {
      newErrors.description = 'Description is required for duplicate engagements';
    }

    // Cross-field validation for duplicate mode
    // Check if Client Name + Tax Year-End + Description already exists
    if (isDuplicateMode && !newErrors.clientName && !newErrors.taxYearEnd && !newErrors.description) {
      const existing = await db.engagements
        .where('clientName')
        .equals(formData.clientName!)
        .toArray();

      const exactDuplicate = existing.find(
        (e) =>
          e.taxYearEnd === formData.taxYearEnd &&
          e.description === formData.description?.trim() &&
          e.id !== engagement?.id
      );

      if (exactDuplicate) {
        newErrors.description = 
          'An engagement with this Name, Year-End, and Description already exists. Please provide a unique description for this duplicate.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (isSubmitting) return;

    const isValid = await validateForm();
    if (!isValid) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    setIsSubmitting(true);

    try {
      const engagementData: Engagement = {
        id: isEditMode ? engagement?.id : crypto.randomUUID(),
        clientName: formData.clientName!,
        taxYearEnd: formData.taxYearEnd!,
        taxForm: formData.taxForm!,
        description: formData.description || undefined,
        createdAt: isEditMode ? engagement!.createdAt : Date.now(),
      };

      await onSave(engagementData);
      onClose();
    } catch (error) {
      console.error('Error saving engagement:', error);
      setErrors({ form: 'Failed to save engagement. Please try again.' });
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateInput = (value: string, inputType?: string) => {
    // If the input is in ISO format (YYYY-MM-DD) from native date picker
    if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Convert ISO format to MM/DD/YYYY
      const [year, month, day] = value.split('-');
      const formattedDate = `${month}/${day}/${year}`;
      setFormData({ ...formData, taxYearEnd: formattedDate });
      return;
    }
    
    // If backspace/delete, don't auto-format - let user delete freely
    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
      setFormData({ ...formData, taxYearEnd: value });
      return;
    }
    
    // Extract only digits from input
    const digitsOnly = value.replace(/\D/g, '');
    
    // If user has entered exactly 8 digits, format as MM/DD/YYYY
    if (digitsOnly.length === 8) {
      const formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
      setFormData({ ...formData, taxYearEnd: formatted });
      return;
    }
    
    // Otherwise, just store what they typed (allows partial entry)
    setFormData({ ...formData, taxYearEnd: value });
  };

  const handleCalendarClick = () => {
    if (hiddenDateInputRef.current) {
      hiddenDateInputRef.current.showPicker();
    }
  };

  const handleHiddenDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isoDate = e.target.value; // YYYY-MM-DD format
    if (isoDate) {
      handleDateInput(isoDate);
    }
  };

  const title =
    mode === 'new'
      ? 'New Engagement'
      : mode === 'edit'
      ? 'Edit Engagement'
      : 'Duplicate Engagement';

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 transition-colors duration-300">
      <div
        className={`bg-white dark:bg-neutral-900 rounded-lg shadow-2xl max-w-md w-full transition-colors duration-300 ${
          shake ? 'animate-shake' : ''
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-neutral-700">
          <h2 className="text-xl font-semibold text-taxtrail-text dark:text-taxtrail-dark-text">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5 text-taxtrail-red dark:text-red-400" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Form-level error */}
          {errors.form && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded flex items-center gap-2 text-taxtrail-red dark:text-red-400 transition-colors duration-300">
              <CircleAlert className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{errors.form}</p>
            </div>
          )}

          {/* Client Name */}
          <div>
            <label className="block text-sm font-medium mb-1 text-taxtrail-text dark:text-taxtrail-dark-text">
              Client Name<span className="text-taxtrail-red dark:text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) =>
                setFormData({ ...formData, clientName: e.target.value })
              }
              disabled={isDuplicateMode || isSubmitting}
              className={`w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 bg-white dark:bg-neutral-800 text-taxtrail-text dark:text-taxtrail-dark-text transition-colors duration-300 ${
                errors.clientName ? 'border border-taxtrail-red dark:border-red-400' : 'border border-gray-300 dark:border-neutral-600'
              } ${isDuplicateMode ? 'bg-gray-100 dark:bg-neutral-700 cursor-not-allowed' : ''}`}
              placeholder="Enter client name"
            />
            {errors.clientName && (
              <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                <CircleAlert className="w-3 h-3" />
                {errors.clientName}
              </p>
            )}
          </div>

          {/* Tax Year-End */}
          <div>
            <label className="block text-sm font-medium mb-1 text-taxtrail-text dark:text-taxtrail-dark-text">
              Tax Year-End<span className="text-taxtrail-red dark:text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                ref={dateInputRef}
                type="text"
                value={
                  formData.taxYearEnd && formData.taxYearEnd.includes('-')
                    ? formatDate(formData.taxYearEnd)
                    : formData.taxYearEnd
                }
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  const nativeEvent = e.nativeEvent as InputEvent;
                  handleDateInput(target.value, nativeEvent.inputType);
                }}
                disabled={isEditMode || isDuplicateMode || isSubmitting}
                className={`w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 bg-white dark:bg-neutral-800 text-taxtrail-text dark:text-taxtrail-dark-text transition-colors duration-300 ${
                  errors.taxYearEnd ? 'border border-taxtrail-red dark:border-red-400' : 'border border-gray-300 dark:border-neutral-600'
                } ${isEditMode || isDuplicateMode ? 'bg-gray-100 dark:bg-neutral-700 cursor-not-allowed' : ''}`}
                placeholder="mm/dd/yyyy"
              />
              {/* Hidden date input for native date picker */}
              <input
                ref={hiddenDateInputRef}
                type="date"
                onChange={handleHiddenDateChange}
                className="absolute opacity-0 pointer-events-none"
                tabIndex={-1}
                aria-hidden="true"
              />
              {!isEditMode && !isDuplicateMode && (
                <button
                  type="button"
                  onClick={handleCalendarClick}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Open date picker"
                >
                  <Calendar className="w-4 h-4" />
                </button>
              )}
            </div>
            {errors.taxYearEnd && (
              <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                <CircleAlert className="w-3 h-3" />
                {errors.taxYearEnd}
              </p>
            )}
          </div>

          {/* Tax Form */}
          <div>
            <label className="block text-sm font-medium mb-1 text-taxtrail-text dark:text-taxtrail-dark-text">
              Tax Form<span className="text-taxtrail-red dark:text-red-400">*</span>
            </label>
            <select
              value={formData.taxForm}
              onChange={(e) =>
                setFormData({ ...formData, taxForm: e.target.value })
              }
              disabled={isEditMode || isDuplicateMode || isSubmitting}
              className={`w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 bg-white dark:bg-neutral-800 text-taxtrail-text dark:text-taxtrail-dark-text transition-colors duration-300 ${
                errors.taxForm ? 'border border-taxtrail-red dark:border-red-400' : 'border border-gray-300 dark:border-neutral-600'
              } ${isEditMode || isDuplicateMode ? 'bg-gray-100 dark:bg-neutral-700 cursor-not-allowed' : ''}`}
            >
              <option value="">Select a tax form...</option>
              {TAX_FORMS.map((form) => (
                <option key={form} value={form}>
                  {form}
                </option>
              ))}
            </select>
            {errors.taxForm && (
              <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                <CircleAlert className="w-3 h-3" />
                {errors.taxForm}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1 text-taxtrail-text dark:text-taxtrail-dark-text">
              Description{isDuplicateMode && <span className="text-taxtrail-red dark:text-red-400">*</span>}
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              disabled={isSubmitting}
              className={`w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 bg-white dark:bg-neutral-800 text-taxtrail-text dark:text-taxtrail-dark-text transition-colors duration-300 ${
                errors.description ? 'border border-taxtrail-red dark:border-red-400' : 'border border-gray-300 dark:border-neutral-600'
              }`}
              placeholder={isDuplicateMode ? "Required for duplicate" : "Optional description"}
            />
            {errors.description && (
              <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                <CircleAlert className="w-3 h-3" />
                {errors.description}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-neutral-700 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="px-6 py-2 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 focus:ring-offset-2"
            style={{ backgroundColor: '#1a2e1a', color: '#ffffff' }}
            onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#152415')}
            onMouseLeave={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#1a2e1a')}
          >
            {isSubmitting ? 'Saving...' : 'Save Engagement'}
          </button>
        </div>
      </div>
    </div>
  );
};

